import { inject, Injectable } from "@angular/core";
import { AccessLevel, AccountMarketplace, Currency, OnboardingCallStatus, Plan } from "@front/m19-api-client";
import {
  FREE_TEST_AD_SPEND_THRESHOLD_EUROS,
  isInFreeTestAndHasNotSubscribed,
  OrganizationAccountGroups,
} from "@front/m19-models";
import { AuthService, BillingService, OrganizationAccountGroupService, StatsService } from "@front/m19-services";
import { ModalService } from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { UpgradeToProFromAutopilotModalComponent } from "@m19-board/onboarding/upgrade-to-pro-from-autopilot-modal.component";
import { DateTime } from "luxon";
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  mergeWith,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
} from "rxjs";

export enum OnboardingStatus {
  FreeTestSkipped = "free-test-skipped",
  OnboardingCallSkipped = "onboarding-call-skipped",
  OnboardingCallScheduled = "onboarding-call-scheduled",
  OnboardingCallDone = "onboarding-call-done",
  StartedFreeTest = "started-free-test",
  FreeTestExpired = "free-test-expired",
  StarterPlan = "starter-plan",
  Done = "done",
  WhitelabelSelfServiceNewUser = "whitelabel-self-service-new-user",
  WhitelabelSelfServiceStarter = "whitelabel-self-service-starter",
  MissingBillingInfo = "missing-billing-info",
  ProductTour = "product-tour",
}

export enum OnboardingStep {
  AccountLinking = "account-linking",
  StatsDownloading = "downloading-stats",
  OnboardingCall = "onboarding-call",
  FreeTestSubscription = "free-test-subscription",
  Skipped = "skipped",
  WhitelabelNoAccount = "whitelabel-no-account",
  NoAccount = "no-account",
  Done = "done",
}

export const OnBoardingStepRedirects: Record<OnboardingStep, string | true> = {
  [OnboardingStep.AccountLinking]: "/onboarding/account-linking",
  [OnboardingStep.StatsDownloading]: "/onboarding/downloading-stats",
  [OnboardingStep.OnboardingCall]: "/onboarding/onboarding-call",
  [OnboardingStep.FreeTestSubscription]: "/onboarding/free-test-subscription",
  [OnboardingStep.WhitelabelNoAccount]: "/onboarding/agency-no-account",
  [OnboardingStep.NoAccount]: "/onboarding/no-account",
  [OnboardingStep.Skipped]: true,
  [OnboardingStep.Done]: true,
};

@Injectable({
  providedIn: "root",
})
export class OnboardingService {
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly statsService = inject(StatsService);
  private readonly layoutSelectorService = inject(LayoutSelectorService);
  private readonly billingService = inject(BillingService);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly skippedOnboarding$ = new BehaviorSubject<boolean>(false);
  private inOnboardingFlow = false;

  private readonly organizationGroups$ = this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
    filter((a) => !!a),
  );

  private readonly accountMarketplacesCost$ = this.organizationGroups$.pipe(
    switchMap((organizations) =>
      organizations && organizations!.length > 0 && organizations![0].organization?.adStatsReadyDatetime !== undefined
        ? this.statsService.getAdStatsPerAccountMarketplace(
            organizations![0].accountGroups.flatMap((ag) =>
              ag.resources
                .filter((r) => r.hasAccessToAdvertising)
                .map((r) => ({ accountId: r.accountId, marketplace: r.marketplace }) as AccountMarketplace),
            ),
            DateUtils.formatDateForApiFromToday(-30),
            DateUtils.formatDateForApiFromToday(-1),
            Currency.EUR,
          )
        : of([]),
    ),
    map((adStats) => {
      const costPerMarketplace = new Map<string, number>();
      adStats.forEach((stat) => {
        const key = `${stat.accountId}-${stat.marketplace}`;
        if (!costPerMarketplace.has(key)) {
          costPerMarketplace.set(key, 0);
        }
        costPerMarketplace.set(key, costPerMarketplace.get(key)! + (stat.cost ?? 0));
      });
      return costPerMarketplace;
    }),
  );

  public readonly belowFreeTestAdSpendThreshold$: Observable<boolean> = this.accountMarketplacesCost$.pipe(
    map((costPerMarketplace) => {
      for (const cost of costPerMarketplace.values()) {
        if (cost >= FREE_TEST_AD_SPEND_THRESHOLD_EUROS) {
          return false;
        }
      }
      return true;
    }),
    shareReplay(1),
  );

  public readonly isBillingOwner$: Observable<boolean> = combineLatest([
    this.billingService.organizationOwner$,
    this.authService.loggedUser$,
  ]).pipe(map(([organization, user]) => organization?.organization.organizationId === user!.ownedOrganizationId));

  public readonly currentStep$: Observable<OnboardingStep> = combineLatest([
    this.skippedOnboarding$,
    this.organizationGroups$,
    this.belowFreeTestAdSpendThreshold$,
  ]).pipe(
    map(([skippedOnboarding, organizationAccountGroups, belowFreeTestAdSpendThreshold]) => {
      if (organizationAccountGroups!.length === 0 || organizationAccountGroups.every((a) => !a.hasAccounts())) {
        if (this.layoutSelectorService.getBoardType() == BoardType.WHITELABEL) {
          return OnboardingStep.WhitelabelNoAccount;
        }

        if (
          organizationAccountGroups.some(
            (o) => o.hasActiveSubscription() && !isInFreeTestAndHasNotSubscribed(o.organization, false),
          )
        ) {
          return OnboardingStep.NoAccount;
        }

        this.inOnboardingFlow = true;
        return OnboardingStep.AccountLinking;
      }
      const organizations = organizationAccountGroups!.filter((o) => o.accessLevel === AccessLevel.ADMIN);
      if (organizations.length !== 1) {
        // admins of more than one organization or only delegated access
        // in that case do not go through onboarding flow
        return OnboardingStep.Done;
      }
      const organization = organizations[0];
      if (organization.organization.billingPlan) {
        // user has a billing plan
        return OnboardingStep.Done;
      }
      if (!organization.organization.adStatsReadyDatetime) {
        this.inOnboardingFlow = true;
        return OnboardingStep.StatsDownloading;
      }
      // check that the organization is entitled for a free test subscription
      if (!this.entitledForFreeTestSubscription(organization)) {
        return OnboardingStep.Done;
      }
      if (skippedOnboarding) {
        return OnboardingStep.Skipped;
      }
      if (!this.inOnboardingFlow) {
        return OnboardingStep.Done;
      }

      // after stats download, check the ad spend over the last 30 days
      if (belowFreeTestAdSpendThreshold) {
        return OnboardingStep.FreeTestSubscription;
      }
      switch (organization.organization.onboardingCallStatus) {
        case OnboardingCallStatus.SCHEDULED:
          return OnboardingStep.Done;
        case OnboardingCallStatus.DONE:
          return OnboardingStep.FreeTestSubscription;
        default:
          return OnboardingStep.OnboardingCall;
      }
    }),
  );

  public readonly remainingDaysForFreeTest$ = this.organizationGroups$.pipe(
    map((organizationAccountGroups) => {
      if (!organizationAccountGroups || organizationAccountGroups.length !== 1) {
        return 0;
      }
      const organization = organizationAccountGroups![0].organization;
      if (organization.testEndDate) {
        return DateUtils.getDateIntervalInDays([DateTime.utc().toISO(), organization.testEndDate]);
      }
      return 0;
    }),
  );
  public onboardingCallDatetimeUpdate = new Subject<string | undefined>();
  public readonly onboardingCallDatetime$: Observable<string | undefined> = this.organizationGroups$.pipe(
    map((org) => {
      if (org.length !== 1) {
        return undefined;
      }
      return DateTime.fromISO(org[0].organization.onboardingCallDatetime!, { zone: "utc" }).toLocal().toISO()!;
    }),
    mergeWith(this.onboardingCallDatetimeUpdate),
    shareReplay(1),
  );

  public readonly currentStatus$: Observable<OnboardingStatus> = combineLatest([
    this.organizationGroups$,
    this.belowFreeTestAdSpendThreshold$,
    this.currentStep$,
    this.authService.isProductTour$,
  ]).pipe(
    map(([organizationAccountGroups, belowFreeTestAdSpendThreshold, , isProductTour]) => {
      if (isProductTour) {
        return OnboardingStatus.ProductTour;
      }
      if (!organizationAccountGroups || organizationAccountGroups.length !== 1) {
        return OnboardingStatus.Done;
      }
      const organization = organizationAccountGroups[0].organization;
      if (this.layoutSelectorService.getBoardType() == BoardType.WHITELABEL_SELFSERVICE) {
        if (organization.billingPlan === undefined) {
          return OnboardingStatus.WhitelabelSelfServiceNewUser;
        }
        if (organization.billingPlan.plan === Plan.STARTER) {
          return OnboardingStatus.WhitelabelSelfServiceStarter;
        }
        return OnboardingStatus.Done;
      }
      if (!organization.billingPlan && this.entitledForFreeTestSubscription(organizationAccountGroups[0])) {
        if (belowFreeTestAdSpendThreshold) {
          return OnboardingStatus.FreeTestSkipped;
        }
        if (!organization.onboardingCallStatus) {
          return OnboardingStatus.OnboardingCallSkipped;
        }
        if (organization.onboardingCallStatus === OnboardingCallStatus.SCHEDULED) {
          return OnboardingStatus.OnboardingCallScheduled;
        }
        if (organization.onboardingCallStatus === OnboardingCallStatus.DONE) {
          return OnboardingStatus.OnboardingCallDone;
        }
      }
      if (!organization.billingPlan || organization.billingPlan?.plan === Plan.STARTER) {
        return OnboardingStatus.StarterPlan;
      }

      if (organization.billingPlan?.plan == Plan.FREE_TEST && organization.testEndDate) {
        const isFreeTestOver =
          DateTime.fromISO(organization.testEndDate, { zone: "utc" }) < DateTime.utc().startOf("day");
        if (isFreeTestOver) {
          const hasUpgraded = organization.freeTestUpgradeBillingPlanId === undefined;
          if (hasUpgraded) {
            return OnboardingStatus.Done;
          }
          return OnboardingStatus.FreeTestExpired;
        }
        if (!organization.freeTestUpgradeDate) {
          return OnboardingStatus.StartedFreeTest;
        }
        if (organization.freeTestUpgradeDate && !organization.customerId) {
          return OnboardingStatus.MissingBillingInfo;
        }
      }
      return OnboardingStatus.Done;
    }),
  );

  public isPreFreeTest$ = this.currentStatus$.pipe(
    map((status) =>
      [
        OnboardingStatus.FreeTestSkipped,
        OnboardingStatus.OnboardingCallSkipped,
        OnboardingStatus.OnboardingCallScheduled,
        OnboardingStatus.OnboardingCallDone,
      ].includes(status),
    ),
  );

  public hasSkippedOnboardingCall$ = this.currentStatus$.pipe(
    map((status) => status === OnboardingStatus.OnboardingCallSkipped),
  );

  public hasScheduledOnboardingCall$ = this.currentStatus$.pipe(
    map((status) => status === OnboardingStatus.OnboardingCallScheduled),
  );

  public entitledForFreeTestSubscription(organization: OrganizationAccountGroups) {
    if (this.layoutSelectorService.getBoardType() !== BoardType.M19) {
      return false;
    }
    // check that the organization has never run a free test
    if (organization.organization.testEndDate) {
      return false;
    }
    // check that organization resources have never run a free test
    const accountMarketplaces = organization.accountGroups.flatMap((ag) => ag.resources);
    if (accountMarketplaces.some((am) => am.freeTestStartDate !== undefined)) {
      return false;
    }
    return true;
  }

  public upgradeFromProductTour() {
    this.modalService.openModal(UpgradeToProFromAutopilotModalComponent, {
      size: "lg",
      modalTitleIcon: "arrowUp",
      modalTitle: this.translocoService.translate("product-tour.upgrade_to_pro"),
    });
  }

  public skipOnboarding(skip = true) {
    this.skippedOnboarding$.next(skip);
    this.inOnboardingFlow = false;
  }
}
